import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Download, Search, Loader2, AlertCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { formatMoney, formatDate } from '../../../utils/formatters';
import { rowsToCsv, downloadCsv, centsToDollarsRaw } from '../csvHelpers';
import { listPoAuditSummary } from '../../../api/mec';
import { formatDelta } from './poAuditHelpers';
import PoAuditAccordion from './PoAuditAccordion';

// Top-tier PO audit table — one row per PO. Click any row to inline-expand
// a PoAuditAccordion with the per-vendor reconciliation below.
//
// Default sort: abs(delta_cents) DESC so the worst offenders bubble up.
// Header clicks cycle: DESC → ASC → DESC. Hold Shift to sort by other cols.

const COLUMNS = [
  { key: 'po_number',     label: 'PO #',       align: 'left',  sortable: true, width: 110 },
  { key: 'ro_numbers',    label: 'RO #',       align: 'left',  sortable: false                },
  { key: 'qbo_bill_count',label: '# Bills',    align: 'right', sortable: true, width: 70  },
  { key: 'vendor_count',  label: 'Vendors',    align: 'right', sortable: true, width: 80  },
  { key: 'qbo_total',     label: 'QBO $',      align: 'right', sortable: true, width: 110, sortField: 'qbo_total_cents' },
  { key: 'tek_loaded',    label: 'Tek $',      align: 'right', sortable: true, width: 110, sortField: 'tek_loaded_cents' },
  { key: 'delta',         label: 'Δ',          align: 'right', sortable: true, width: 110, sortField: 'delta_cents' },
  { key: 'latest_date',   label: 'Latest',     align: 'right', sortable: true, width: 100, sortField: 'latest_qbo_date' },
  { key: 'expander',      label: '',           align: 'right', sortable: false, width: 36  },
];

export default function PoAuditSummaryTable({
  closeRunId,
  method,
  domain = 'parts',
  title,
  description,
  emptyMessage,
  csvFilename,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [expandedPo, setExpandedPo] = useState(null);

  // Default: sort by abs(delta_cents) DESC.
  const [sortKey, setSortKey] = useState('abs_delta_cents');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!closeRunId) {
      setRows([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    setExpandedPo(null);
    listPoAuditSummary(closeRunId, method, domain)
      .then((data) => {
        if (!alive) return;
        setRows(data?.pos || []);
      })
      .catch((err) => {
        if (!alive) return;
        if (/404|not found/i.test(err?.message || '')) {
          setRows([]);
        } else {
          setError(err?.message || 'Failed to load PO audit summary');
          setRows([]);
        }
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [closeRunId, method, domain]);

  const filtered = useMemo(() => {
    if (!searchText) return rows;
    const needle = searchText.toLowerCase();
    return rows.filter((r) => {
      if ((r.po_number || '').toLowerCase().includes(needle)) return true;
      if ((r.ro_numbers || []).some((ro) => String(ro).toLowerCase().includes(needle))) return true;
      return false;
    });
  }, [rows, searchText]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const aVal = sortValue(a, sortKey);
      const bVal = sortValue(b, sortKey);
      let cmp;
      if (aVal == null && bVal == null) cmp = 0;
      else if (aVal == null) cmp = 1;
      else if (bVal == null) cmp = -1;
      else if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
      else cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const onSort = (col) => {
    if (!col.sortable) return;
    const field = col.sortField || col.key;
    if (sortKey === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(field);
      setSortDir('desc');
    }
  };

  const totalDeltaCents = useMemo(
    () => filtered.reduce((s, r) => s + (r.delta_cents || 0), 0),
    [filtered]
  );

  const exportCsv = () => {
    const csv = rowsToCsv(sorted, [
      { key: 'po_number',      label: 'PO #' },
      { key: 'ro_numbers',     label: 'RO #s', accessor: (r) => (r.ro_numbers || []).join('; ') },
      { key: 'qbo_bill_count', label: '# QBO Bills' },
      { key: 'vendor_count',   label: '# Vendors' },
      { key: 'qbo_total',      label: 'QBO Total',  accessor: (r) => centsToDollarsRaw(r.qbo_total_cents) },
      { key: 'tek_loaded',     label: 'Tek Loaded', accessor: (r) => centsToDollarsRaw(r.tek_loaded_cents) },
      { key: 'delta',          label: 'Δ Amount',   accessor: (r) => centsToDollarsRaw(r.delta_cents) },
      { key: 'latest_qbo_date',   label: 'Latest QBO Date' },
      { key: 'earliest_qbo_date', label: 'Earliest QBO Date' },
    ]);
    downloadCsv(csvFilename || `mec-po-audit-${method}-${domain}.csv`, csv);
  };

  return (
    <Card padded={false}>
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          {title && (
            <h3 className="text-[15px] font-semibold text-[var(--color-warm-900)] leading-tight">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-[12px] text-[var(--color-warm-500)] mt-0.5">
              {description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-warm-400)] pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search PO# or RO#…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="h-8 pl-7 pr-3 text-[12px] bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] focus:outline-none focus:border-[var(--color-warm-400)] w-56"
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download size={12} />}
            onClick={exportCsv}
            disabled={sorted.length === 0}
          >
            CSV
          </Button>
        </div>
      </div>

      <div className="px-5 pb-2 text-[11px] text-[var(--color-warm-500)] flex items-center gap-2 flex-wrap">
        {sorted.length} PO{sorted.length === 1 ? '' : 's'}
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="tabular-nums font-semibold text-[var(--color-warm-700)]">
          Net Δ {formatDelta(totalDeltaCents)}
        </span>
        {searchText && filtered.length !== rows.length && (
          <>
            <span className="text-[var(--color-warm-300)]">·</span>
            <span>filtered from {rows.length}</span>
          </>
        )}
      </div>

      {error && (
        <div className="mx-5 mb-3 flex items-start gap-2 px-3 py-2 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] text-[12px] text-[var(--color-danger-700)] border border-[var(--color-danger-100)]">
          <AlertCircle size={13} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="overflow-x-auto border-t border-[var(--color-warm-100)]">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-[var(--color-cream-100)] border-b border-[var(--color-warm-100)]">
              {COLUMNS.map((c) => {
                const field = c.sortField || c.key;
                const active = sortKey === field;
                return (
                  <Th
                    key={c.key}
                    align={c.align}
                    width={c.width}
                    sortable={c.sortable}
                    active={active}
                    direction={active ? sortDir : null}
                    onClick={() => onSort(c)}
                  >
                    {c.label}
                  </Th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-5 py-10 text-center">
                  <div className="inline-flex items-center gap-2 text-[12px] text-[var(--color-warm-500)]">
                    <Loader2 size={13} className="animate-spin" />
                    Loading PO audit summary…
                  </div>
                </td>
              </tr>
            )}
            {!loading && sorted.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length} className="px-5 py-10 text-center text-[12px] text-[var(--color-warm-500)]">
                  {emptyMessage || 'Nothing in this bucket for the selected period.'}
                </td>
              </tr>
            )}
            {!loading && sorted.map((r) => {
              const expanded = expandedPo === r.po_number;
              const deltaCents = r.delta_cents || 0;
              return (
                <Fragment key={r.po_number}>
                  <tr
                    onClick={() => setExpandedPo(expanded ? null : r.po_number)}
                    className={`
                      border-b border-[var(--color-warm-100)] cursor-pointer
                      ${expanded ? 'bg-[var(--color-cream-100)]/70' : 'hover:bg-[var(--color-cream-100)]/40'}
                      transition-colors
                    `}
                  >
                    <td className="px-3 py-3 align-middle">
                      <span className="text-[13px] font-semibold mono-nums text-[var(--color-warm-900)]">
                        PO {r.po_number}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span className="text-[12px] mono-nums text-[var(--color-warm-600)]">
                        {(r.ro_numbers || []).length === 0
                          ? <span className="italic text-[var(--color-warm-400)]">—</span>
                          : (r.ro_numbers || []).join(', ')}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums text-[var(--color-warm-700)]">
                      {r.qbo_bill_count ?? 0}
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums text-[var(--color-warm-700)]">
                      {r.vendor_count ?? 0}
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
                      {formatMoney((r.qbo_total_cents || 0) / 100)}
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
                      {formatMoney((r.tek_loaded_cents || 0) / 100)}
                    </td>
                    <td className="px-3 py-3 align-middle text-right tabular-nums">
                      <span className={`text-[12px] font-semibold ${
                        deltaCents === 0
                          ? 'text-[var(--color-warm-500)]'
                          : deltaCents > 0
                            ? 'text-[var(--color-danger-700)]'
                            : 'text-[var(--color-action-700)]'
                      }`}>
                        {formatDelta(deltaCents)}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-[12px] text-[var(--color-warm-600)]">
                      {r.latest_qbo_date ? formatDate(r.latest_qbo_date) : '—'}
                    </td>
                    <td className="px-3 py-3 align-middle text-right text-[var(--color-warm-500)]">
                      {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-[var(--color-cream-100)]/40">
                      <td colSpan={COLUMNS.length} className="p-0">
                        <PoAuditAccordion
                          closeRunId={closeRunId}
                          poNumber={r.po_number}
                          domain={domain}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function sortValue(row, sortKey) {
  if (sortKey === 'abs_delta_cents') return Math.abs(row.delta_cents || 0);
  return row[sortKey];
}

function Th({ children, align = 'left', width, sortable = false, active = false, direction = null, onClick }) {
  return (
    <th
      style={width ? { width: `${width}px` } : undefined}
      onClick={sortable ? onClick : undefined}
      className={`
        px-3 py-2.5 text-[10px] font-bold uppercase tracking-[1px]
        ${active ? 'text-[var(--color-warm-800)]' : 'text-[var(--color-warm-500)]'}
        ${sortable ? 'cursor-pointer select-none hover:text-[var(--color-warm-800)]' : ''}
        ${align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && active && (
          <span className="text-[10px]">{direction === 'asc' ? '▲' : '▼'}</span>
        )}
      </span>
    </th>
  );
}
